// /users/:id — Customer 360 (FACTS PACK §3b).
//  • profile card
//  • lifecycle action bar (UserActionBar) — sensitive actions confirm + audited
//  • owned/shared devices (subscription is PER-DEVICE, nested) → link to /devices/:id
//  • recent events (~10 newest)
//
// Replaces the scaffold's placeholder (owner explicitly invited a richer version).
// Styling reuses the shared console classes + adm-* detail helpers (_shared/shared.css)
// + <StateBlock>; only the device-card grid / cell truncation are feature-local
// (users.css, u-* additive). See return notes.
//
// REDESIGN(2026-07-10, Graphite Operations Deck):
//  • 版面改為「sticky 左欄(基本資料/處置/診斷鎖定) + tab 化右欄(總覽/裝置/事件/紀錄)」,
//    取代原本 7 段直向堆疊(實機曾量到 ~5791px 高)。左欄用共用 .adm-detail-grid.cols-2 +
//    .adm-sticky-col(shared.css 既有,devices/:id 已在用,非新增共用件)。
//  • 右欄 tab 用共用 .adm-tabs/.adm-tab(shared.css 既有內容 tab 語彙),純 React state、
//    不寫入 URL、不動路由。「總覽」預設只渲染裝置前 3 卡 + 事件前 5 筆,長清單(14 裝置/
//    70 事件)收進各自 tab、切換時才渲染,避免整頁一次塞爆。
//  • mono ID(使用者ID/裝置ID/關聯ID)改用共用 .adm-copy-mono + .adm-copy-btn(shared.css
//    既有,hover 顯示複製鍵),客服排查可一鍵複製不必再手動選字。

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import { formatRelativeUs, formatTimestampUs, shortId } from '../../lib/format';
import { AuditTrailPanel } from '../audit/AuditTrailPanel';
import { DiagnosticLockPanel } from '../diagnostics/DiagnosticLockPanel';
import { UserActionBar } from './UserActionBar';
import { UserEcosystemPanel } from './UserEcosystemPanel';
import {
  deviceName,
  deviceOnlineLabel,
  deviceStatusLabel,
  eventTypeLabel,
  roleLabel,
  subStatusLabel,
  userStatusLabel,
  userStatusTone,
} from './userModel';
import { getUserDetail } from './usersApi';
import type { AdminUser, RecentEvent, UserDetail, UserDevice } from './usersApi';
import './users.css';

type DetailTab = 'overview' | 'devices' | 'events' | 'audit';

// 加入的生態系 = 顯式聯集成員 + kinio.ai 平台聯盟旗標(平台用戶本身不列聯盟)。
function joinedEcosystemsLabel(u: AdminUser): string {
  const names = (u.joined_ecosystems ?? []).map((e) => e.name || e.id);
  if (u.platform_union && u.base_ecosystem_id !== 'eco-kinio-ai') names.push('kinio.ai 平台聯盟');
  return names.length > 0 ? names.join('、') : '—';
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const userId = id ?? '';
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [tab, setTab] = useState<DetailTab>('overview');
  const alive = useRef(true);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getUserDetail(userId);
      if (alive.current) setDetail(d);
    } catch (e) {
      if (alive.current) setError(e);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const headTitle = detail?.user.display_name || detail?.user.email || '使用者詳情';
  // Deep-link into the audit console pre-filtered to this user as the target axis
  // (AuditPage reads ?target_id=user:<id> on mount → 目標使用者 mode). Mirrors the
  // device-360 audit jump so both 360 views reach the same targeted audit search.
  const auditHref = `/audit?target_id=${encodeURIComponent(`user:${userId}`)}`;

  return (
    <>
      <Link to="/users" className="adm-back">
        ← 返回使用者
      </Link>

      <div className="page-header">
        <div>
          <h1 className="page-title">{headTitle}</h1>
          <p className="page-subtitle mono">{userId}</p>
        </div>
        {detail && (
          <div className="u-pill-row">
            <span className={`pill pill-${userStatusTone(detail.user.status)}`}>{userStatusLabel(detail.user.status)}</span>
            <span className="pill pill-neutral">{roleLabel(detail.user.role)}</span>
            {detail.user.region && <span className="pill pill-neutral">{detail.user.region}</span>}
          </div>
        )}
      </div>

      {loading && !detail && <LoadingState label="載入客戶 360…" />}
      {error != null && !detail && <ErrorState error={error} onRetry={() => void load()} />}

      {detail && (
        <div className="adm-detail-grid cols-2">
          {/* 左欄:sticky,客服往下捲右側長清單時,狀態/處置/診斷鎖定全程可見 */}
          <div className="adm-sticky-col">
            {/* profile */}
            <section className="card">
              <h2 className="adm-card-title">基本資料</h2>
              <dl className="adm-kv">
                <dt>使用者 ID</dt>
                <dd className="adm-mono">
                  <CopyableId value={detail.user.user_id} display={detail.user.user_id} />
                </dd>
                <dt>電子郵件</dt>
                <dd>{detail.user.email || '—'}</dd>
                <dt>手機</dt>
                <dd>
                  {detail.user.phone_e164 || '—'}
                  {detail.user.phone_e164 && !detail.user.phone_verified ? '（未驗證）' : ''}
                </dd>
                <dt>顯示名稱</dt>
                <dd>{detail.user.display_name || '—'}</dd>
                <dt>地區</dt>
                <dd>{detail.user.region || '—'}</dd>
                <dt>基礎生態系</dt>
                <dd title={detail.user.base_ecosystem_id || detail.user.family_id || undefined}>
                  {detail.user.base_ecosystem_name || detail.user.family_name || detail.user.base_ecosystem_id || '—'}
                </dd>
                <dt>加入生態系</dt>
                <dd>{joinedEcosystemsLabel(detail.user)}</dd>
                <dt>角色</dt>
                <dd>{roleLabel(detail.user.role)}</dd>
                <dt>狀態</dt>
                <dd>{userStatusLabel(detail.user.status)}</dd>
                <dt>建立時間</dt>
                <dd className="adm-mono">{formatTimestampUs(detail.user.created_at)}</dd>
              </dl>
            </section>

            {/* lifecycle actions(治理/高風險分組見 UserActionBar 內部) */}
            <UserActionBar user={detail.user} onChanged={() => void load()} />

            {/* 原生態系歸屬(operator 改正誤歸屬;restful#84) */}
            <UserEcosystemPanel user={detail.user} onChanged={() => void load()} />

            <DiagnosticLockPanel targetType="user" targetId={userId} />

            {/* Customer-360 → 合規:帳號刪除 / DSAR 深連(預填 user_id,比照稽核軌跡深連) */}
            <section className="card adm-actionbar">
              <div className="adm-actionbar-head">
                <h2 className="adm-card-title">帳號刪除 / DSAR</h2>
              </div>
              <p className="text-secondary adm-inline-note">
                代理此使用者送出帳號刪除或資料匯出(DSAR)申請;寫入動作需於合規頁填寫原因與案件編號。
              </p>
              <div className="adm-actionbar-btns">
                <Link
                  className="btn-ghost btn-sm"
                  to={`/compliance?user_id=${encodeURIComponent(userId)}`}
                  data-testid="user-compliance-link"
                >
                  前往帳號刪除 / DSAR
                </Link>
              </div>
            </section>
          </div>

          {/* 右欄:內容 tab(純本地 state,不動路由),長清單只在切到對應 tab 才渲染 */}
          <div>
            <div className="adm-tabs" role="tablist" aria-label="客戶 360 內容分頁">
              <button role="tab" aria-selected={tab === 'overview'} className={`adm-tab${tab === 'overview' ? ' is-active' : ''}`} onClick={() => setTab('overview')}>
                總覽
              </button>
              <button role="tab" aria-selected={tab === 'devices'} className={`adm-tab${tab === 'devices' ? ' is-active' : ''}`} onClick={() => setTab('devices')}>
                裝置（{detail.devices.length}）
              </button>
              <button role="tab" aria-selected={tab === 'events'} className={`adm-tab${tab === 'events' ? ' is-active' : ''}`} onClick={() => setTab('events')}>
                近期事件（{detail.recent_events.length}）
              </button>
              <button role="tab" aria-selected={tab === 'audit'} className={`adm-tab${tab === 'audit' ? ' is-active' : ''}`} onClick={() => setTab('audit')}>
                處置紀錄
              </button>
            </div>

            <div className="u-tab-body">
              {tab === 'overview' && (
                <OverviewTab detail={detail} onSeeDevices={() => setTab('devices')} onSeeEvents={() => setTab('events')} />
              )}

              {tab === 'devices' && (
                <section>
                  {detail.devices.length === 0 ? (
                    <div className="adm-inline-empty">
                      <span className="adm-inline-empty-icon">🗄️</span>
                      <span>此使用者名下沒有裝置。</span>
                    </div>
                  ) : (
                    <div className="u-device-list">
                      {detail.devices.map((d) => (
                        <DeviceCard key={d.device_id} d={d} />
                      ))}
                    </div>
                  )}
                </section>
              )}

              {tab === 'events' && (
                <section>
                  <div className="u-section-head">
                    <p className="text-tertiary" style={{ margin: 0 }}>最新 {detail.recent_events.length} 筆</p>
                    <Link to={auditHref} className="btn-ghost btn-sm">
                      在稽核紀錄中檢視此使用者 →
                    </Link>
                  </div>
                  {detail.recent_events.length === 0 ? (
                    <div className="adm-inline-empty">
                      <span className="adm-inline-empty-icon">🗒️</span>
                      <span>沒有近期事件。</span>
                    </div>
                  ) : (
                    <div className="table-wrap">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>事件</th>
                            <th>時間</th>
                            <th>關聯 ID</th>
                            <th>內容</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.recent_events.map((ev, i) => (
                            // event_id is the capability-encoded event-CLASS code (restful 00 §19),
                            // NOT unique per row — a run of same-class events (live dev data) shares
                            // it, and duplicate keys are a React console.error (0-error rule).
                            <EventRow key={`${ev.event_id}#${ev.event_ts ?? ''}#${i}`} ev={ev} />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              )}

              {tab === 'audit' && (
                // C6:內嵌處置紀錄(target 預填;case_id chip 深連 /audit)
                <AuditTrailPanel targetId={`user:${userId}`} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** 總覽 tab:裝置/事件各取前幾筆的摘要,長清單留給對應分頁,避免總覽自己又變長頁。 */
function OverviewTab({
  detail,
  onSeeDevices,
  onSeeEvents,
}: {
  detail: UserDetail;
  onSeeDevices: () => void;
  onSeeEvents: () => void;
}) {
  const deviceSample = detail.devices.slice(0, 3);
  const eventSample = detail.recent_events.slice(0, 5);
  return (
    <div className="u-overview-stack">
      <section>
        <div className="u-section-head">
          <h3 className="adm-subtitle">裝置摘要</h3>
          {detail.devices.length > 3 && (
            <button type="button" className="btn-ghost btn-sm" onClick={onSeeDevices}>
              查看全部 {detail.devices.length} 台 →
            </button>
          )}
        </div>
        {deviceSample.length === 0 ? (
          <div className="adm-inline-empty">
            <span className="adm-inline-empty-icon">🗄️</span>
            <span>此使用者名下沒有裝置。</span>
          </div>
        ) : (
          <div className="u-device-list">
            {deviceSample.map((d) => (
              <DeviceCard key={d.device_id} d={d} />
            ))}
          </div>
        )}
      </section>

      <section>
        <div className="u-section-head">
          <h3 className="adm-subtitle">事件摘要</h3>
          {detail.recent_events.length > 5 && (
            <button type="button" className="btn-ghost btn-sm" onClick={onSeeEvents}>
              查看全部 →
            </button>
          )}
        </div>
        {eventSample.length === 0 ? (
          <div className="adm-inline-empty">
            <span className="adm-inline-empty-icon">🗒️</span>
            <span>沒有近期事件。</span>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>事件</th>
                  <th>時間</th>
                  <th>關聯 ID</th>
                  <th>內容</th>
                </tr>
              </thead>
              <tbody>
                {eventSample.map((ev, i) => (
                  <EventRow key={`${ev.event_id}#${ev.event_ts ?? ''}#${i}`} ev={ev} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function DeviceCard({ d }: { d: UserDevice }) {
  const sub = d.subscription;
  const online = d.online?.state;
  return (
    <div className="card u-device-card">
      <div className="u-device-top">
        <Link className="u-device-name" to={`/devices/${encodeURIComponent(d.device_id)}`}>
          {deviceName(d)}
        </Link>
        <span className={`pill ${online === 'online' ? 'pill-good' : 'pill-neutral'}`}>{deviceOnlineLabel(online)}</span>
      </div>
      {/* 摘要視圖:一眼看完最常被問的三件事(型號/狀態/訂閱到期),其餘收進「更多細節」 */}
      <dl className="adm-kv u-kv-compact">
        <dt>裝置 ID</dt>
        <dd className="adm-mono">
          <CopyableId value={d.device_id} display={shortId(d.device_id, 14, 6)} />
        </dd>
        <dt>型號</dt>
        <dd>{d.model || '—'}</dd>
        <dt>狀態</dt>
        <dd>
          {deviceStatusLabel(d.status)}
          {d.online?.stale_hint ? '・狀態可能過期' : ''}
        </dd>
        <dt>訂閱</dt>
        <dd>
          {sub ? (
            <>
              {sub.plan || '—'}・{subStatusLabel(sub.status)}
              {sub.expired_time != null ? `・到期 ${formatTimestampUs(sub.expired_time, { second: undefined })}` : ''}
            </>
          ) : (
            '—'
          )}
        </dd>
      </dl>
      <details className="u-device-more">
        <summary>更多細節</summary>
        <dl className="adm-kv u-kv-compact">
          <dt>韌體</dt>
          <dd>{d.firmware_version || '—'}</dd>
          <dt>關係</dt>
          <dd>{roleLabel(d.role)}</dd>
          {d.recording?.mode && (
            <>
              <dt>錄影</dt>
              <dd>
                {d.recording.mode}
                {d.recording.location ? `・${d.recording.location}` : ''}
                {d.recording.quality ? `・${d.recording.quality}` : ''}
              </dd>
            </>
          )}
          {sub && (sub.auto_renew != null || sub.retention_days != null) && (
            <>
              <dt>訂閱細節</dt>
              <dd>
                {sub.auto_renew != null ? `${sub.auto_renew ? '自動續訂' : '不續訂'}` : ''}
                {sub.retention_days != null ? `・保留 ${sub.retention_days} 天` : ''}
              </dd>
            </>
          )}
          {d.registered_at != null && (
            <>
              <dt>綁定時間</dt>
              <dd className="adm-mono">{formatTimestampUs(d.registered_at, { second: undefined })}</dd>
            </>
          )}
        </dl>
      </details>
    </div>
  );
}

function EventRow({ ev }: { ev: RecentEvent }) {
  const payloadStr = ev.payload && Object.keys(ev.payload).length > 0 ? compactJson(ev.payload) : '—';
  const [expanded, setExpanded] = useState(false);
  const hasPayload = payloadStr !== '—';
  return (
    <>
      <tr className={hasPayload ? 'clickable' : undefined} onClick={hasPayload ? () => setExpanded((v) => !v) : undefined}>
        <td className="u-nowrap">{eventTypeLabel(ev.event_type)}</td>
        <td className="u-nowrap" title={formatTimestampUs(ev.event_ts)}>{formatRelativeUs(ev.event_ts)}</td>
        <td className="adm-mono text-tertiary u-nowrap">
          {ev.correlation_id ? <CopyableId value={ev.correlation_id} display={shortId(ev.correlation_id, 12, 6)} /> : '—'}
        </td>
        <td className="adm-mono u-truncate u-payload" title={hasPayload ? '點擊展開完整內容' : undefined}>
          {payloadStr}
        </td>
      </tr>
      {expanded && hasPayload && (
        <tr>
          <td colSpan={4}>
            <pre className="adm-devlog-output u-payload-full">{payloadStr}</pre>
          </td>
        </tr>
      )}
    </>
  );
}

/** Compact one-line view of an arbitrary event payload. */
function compactJson(obj: Record<string, unknown>): string {
  try {
    return Object.entries(obj)
      .map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
      .join('  ');
  } catch {
    return '—';
  }
}
